import { useAppSelector } from '@app/redux/hooks';
import { getBusinessInfo } from '@app/services/business';
import { getIntegrations } from '@app/services/integrations';
import { getDashboardMetrics, getReviewMetrics } from '@app/services/analytics';
import { getFutureSchedules } from '@app/services/campaigns';
import {
  getOnboardingSteps,
  completeOnboardingStep,
  OnboardingStep,
} from '@app/services/onboarding';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

export const useDashboardHook = () => {
  const businessState = useAppSelector((state) => state.business);
  const accessToken = localStorage.getItem('accessToken');
  const queryClient = useQueryClient();

  // not sure if refetching this every instant is good, so making it stale for at least 2min
  const { data: businessInfo, isLoading } = useQuery({
    queryKey: ['business-info'],
    queryFn: () =>
      getBusinessInfo()
        .then((res) => res.data)
        .catch((err) => console.error(err)),
    refetchOnMount: 'always',
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const { data: integrationsData, isLoading: integrationsLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: () =>
      getIntegrations()
        .then((res) => res.data)
        .catch((err) => console.error(err)),
    enabled: !!accessToken,
  });

  const { data: reviewMetrics, isLoading: reviewMetricsLoading } = useQuery({
    queryKey: ['review-metrics'],
    queryFn: () =>
      getReviewMetrics()
        .then((res) => res.data.data)
        .catch((err) => console.error(err)),
    enabled: !!accessToken,
  });

  const { data: dashboardMetrics, isLoading: dashboardMetricsLoading } = useQuery({
    queryKey: ['dashboard-metrics'],
    queryFn: () =>
        getDashboardMetrics()
        .then((res) => res.data.data)
        .catch((err) => console.error(err)),
    enabled: !!accessToken,
  });

  const { data: futureSchedules, isLoading: schedulesLoading } = useQuery({
    queryKey: ['future-schedules'],
    queryFn: () =>
      getFutureSchedules()
        .then((res) => res.data)
        .catch((err) => console.error(err)),
    enabled: !!accessToken,
  });

  const { data: onboardingData, isLoading: onboardingLoading } = useQuery({
    queryKey: ['onboarding-steps'],
    queryFn: () =>
      getOnboardingSteps(true)
        .then((res) => res.data)
        .catch((err) => console.error(err)),
    enabled: !!accessToken,
  });

  // Get the next pending onboarding step
  const nextPendingStep = onboardingData?.steps?.find(
    (step: OnboardingStep) => !step.completed
  );

  const completeStepMutation = useMutation({
    mutationFn: (stepId: string) => completeOnboardingStep(stepId, true),
    onSuccess: () => {
      // Refetch the onboarding data to update the UI
      queryClient.invalidateQueries({ queryKey: ['onboarding-steps'] });
    },
    onError: (error) => {
      console.error('Error completing step:', error);
      toast.error('Failed to mark step as completed. Please try again.');
    },
  });

  const handleStepAction = (stepId: string) => {
    // Mark the step as completed
    completeStepMutation.mutate(stepId);
  };

  return {
    businessInfo,
    isLoading,
    businessState,
    integrationsData,
    integrationsLoading,
    reviewMetrics,
    reviewMetricsLoading,
    dashboardMetrics,
    dashboardMetricsLoading,
    futureSchedules,
    schedulesLoading,
    onboardingData,
    onboardingLoading,
    nextPendingStep,
    handleStepAction,
    isCompletingStep: completeStepMutation.isPending,
  };
};
